import { useEffect, useRef, useState } from 'react';
import { toAbsoluteUrl } from 'src/_metronic/helpers';
import { APP_DOWNLOAD_PATH, detectMobilePlatform, getStoreUrlForPlatform, isMobileOrTabletDevice } from 'src/modules/auth/app-download';

const DISMISS_STORAGE_KEY = 'vendor_app_banner_dismissed_at';
const DISMISS_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const BODY_CLASS = 'app-download-banner-visible';
const HEIGHT_CSS_VAR = '--app-download-banner-height';

function isDismissed(): boolean {
  try {
    const dismissedAt = Number(localStorage.getItem(DISMISS_STORAGE_KEY));
    return Boolean(dismissedAt) && Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

function saveDismiss() {
  try {
    localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage erişilemezse (ör. Safari gizli mod) banner yalnızca bu oturumda kapanır.
  }
}

const AppDownloadBanner = () => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [href, setHref] = useState(APP_DOWNLOAD_PATH);

  useEffect(() => {
    if (!isMobileOrTabletDevice() || isDismissed()) return;
    setHref(getStoreUrlForPlatform(detectMobilePlatform()) ?? APP_DOWNLOAD_PATH);
    setVisible(true);
  }, []);

  // Header mobilde top:0'a sabit; banner yüksekliği body'ye yazılır ve header/wrapper bu kadar aşağı kaydırılır.
  useEffect(() => {
    const banner = bannerRef.current;
    if (!visible || !banner) return;

    const { body } = document;
    const syncHeight = () => body.style.setProperty(HEIGHT_CSS_VAR, `${banner.offsetHeight}px`);

    syncHeight();
    body.classList.add(BODY_CLASS);
    const observer = new ResizeObserver(syncHeight);
    observer.observe(banner);

    return () => {
      observer.disconnect();
      body.classList.remove(BODY_CLASS);
      body.style.removeProperty(HEIGHT_CSS_VAR);
    };
  }, [visible]);

  if (!visible) return null;

  const handleClose = () => {
    saveDismiss();
    setVisible(false);
  };

  return (
    <div
      ref={bannerRef}
      className="app-download-banner position-fixed top-0 start-0 end-0 d-flex align-items-center ps-2 pe-4"
      role="region"
      aria-label="idefix Satıcı Uygulaması indirme"
    >
      <button type="button" className="btn btn-icon btn-sm w-25px h-25px flex-shrink-0 me-1" onClick={handleClose} aria-label="Kapat">
        <img src={toAbsoluteUrl('/media/icons/custom/close.svg')} alt="" width={12} height={12} />
      </button>

      <div className="d-flex align-items-center flex-grow-1 overflow-hidden gap-3">
        <img src={toAbsoluteUrl('/media/logos/idefix-seller-app.svg')} alt="idefix Satıcı" width={34} height={34} className="flex-shrink-0" />

        <div className="d-flex flex-column flex-grow-1 overflow-hidden">
          <span className="text-white fw-bolder fs-6 text-truncate">Mağazan Her An Yanında!</span>
          <span className="text-white fs-8 lh-sm text-truncate">Siparişlerini dilediğin yerden yönet.</span>
        </div>

        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="app-download-banner__button btn btn-sm btn-white fw-bolder flex-shrink-0 px-7 rounded-pill"
        >
          İndir
        </a>
      </div>
    </div>
  );
};

export default AppDownloadBanner;
